'use client';

import { FC } from 'react';
import { twMerge } from 'tailwind-merge';
import Heading from 'components/common/Heading';
import { useDeviceDetect } from '@/utils/useDeviceDetect';
import { useStore } from 'zustand';
import { useMapStore } from '@/stores/map/mapStore';
import WhereToBuyBtn from '@/components/button/where-to-buy-btn';
import { usePageTheme } from '@/stores/page-theme';
import { BackgroundType, Theme } from '@/types/enhancedSlider';
import { backgrounds } from '@/theme/backgrounds';
import { Splide, SplideSlide } from '@splidejs/react-splide';
import '@splidejs/react-splide/css';
import '@/styles/splide-slider.scss';

interface Product {
  id: number;
  model: string;
  price: number;
  coverageArea?: number;
  thumbnail?: {
    url: string;
  };
}

interface EnhancedSliderProps {
  title?: string;
  subtextTop?: string;
  subtextBottom?: string;
  products: Product[];
  headingSize?: 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | '40px';
  headingColor?: string;
  shadowText?: boolean;
  background?: BackgroundType;
  backgroundColor?: string;
  backgroundImage?: string;
  className?: string;
  pageTheme?: Theme;
  dividerColor?: string;
}

const getHeadingSize = (
  size: EnhancedSliderProps['headingSize']
): number | '40px' => {
  switch (size) {
    case 'h1':
      return 1;
    case 'h2':
      return 2;
    case 'h3':
      return 3;
    case 'h4':
      return 4;
    case 'h5':
      return 5;
    case '40px':
      return '40px';
    default:
      return 3;
  }
};

const EnhancedSlider: FC<EnhancedSliderProps> = ({
  title,
  subtextTop,
  subtextBottom,
  products,
  headingSize = 'h3',
  headingColor,
  shadowText = false,
  background = 'inherit',
  backgroundColor,
  pageTheme,
  dividerColor,
}) => {
  const isMobile = useDeviceDetect();
  const { setModalMapOpen } = useStore(useMapStore, (state) => ({
    setModalMapOpen: state.setModalMapOpen,
  }));
  const { bgColor } = useStore(usePageTheme, (state) => ({
    bgColor: state.bgColor,
  }));

  const getBackgroundStyle = () => {
    if (background === 'solid' && backgroundColor) {
      return { backgroundColor };
    }

    if (background === 'inherit') {
      return {};
    }

    if (background && typeof background === 'string') {
      const standardBackgrounds = [
        'solid',
        'inherit',
        'gradient',
        'gradient-mesh',
        'mesh',
        'image',
      ];
      if (!standardBackgrounds.includes(background)) {
        return backgrounds[background as keyof typeof backgrounds]
          ? { background: backgrounds[background as keyof typeof backgrounds] }
          : { background };
      }
    }

    return {};
  };

  const getTextColor = () => {
    if (headingColor) {
      switch (headingColor) {
        case 'white':
          return 'text-white';
        case 'black':
          return 'text-black';
        case 'blue':
          return 'text-primary-dark';
        default:
          return headingColor;
      }
    }

    return pageTheme === 'purple' || pageTheme === 'black'
      ? 'text-white'
      : 'text-primary-dark';
  };

  return (
    <div
      className={`relative w-full ${background === 'inherit' && !backgroundColor ? bgColor : ''}`}
      style={getBackgroundStyle()}
    >
      <div className='enhanced-slider__container container mx-auto flex flex-col items-center justify-start gap-8 px-5 md:gap-6'>
        {(title || subtextTop || subtextBottom) && (
          <div
            className={`enhanced-slider__header mx-auto w-full pt-28 text-center md:w-8/12 ${headingSize === '40px' ? 'min-h-[400px] pb-16' : ''}`}
          >
            {title && (
              <Heading
                size={getHeadingSize(headingSize)}
                className={twMerge(
                  'enhanced-slider__title',
                  shadowText && 'text-shadow-lg',
                  getTextColor(),
                  headingSize === '40px'
                    ? 'text-[40px] md:text-[60px] lg:text-[80px]'
                    : ''
                )}
              >
                {title}
              </Heading>
            )}
            {(subtextTop || subtextBottom) && (
              <div
                className={`enhanced-slider__subtext mt-16 flex flex-col items-center gap-6 md:mt-12 md:flex-row md:items-center md:justify-center md:gap-0 ${headingSize === '40px' ? 'mt-12 md:mt-[34px]' : ''}`}
              >
                <div className='enhanced-slider__subtext-top flex-1 md:pr-14'>
                  {subtextTop && (
                    <div
                      className={twMerge(
                        'text-center opacity-80 md:text-right',
                        getTextColor()
                      )}
                      style={{ fontSize: '18px' }}
                    >
                      {subtextTop}
                    </div>
                  )}
                </div>
                <div
                  className='enhanced-slider__divider mx-5 h-px w-[100%] shrink-0 opacity-50 md:mx-0 md:h-[92px] md:w-px'
                  style={{
                    backgroundColor:
                      dividerColor ||
                      (pageTheme?.toLowerCase() === 'black' ||
                      pageTheme?.toLowerCase() === 'purple'
                        ? 'white'
                        : 'black'),
                  }}
                ></div>
                <div className='enhanced-slider__subtext-bottom flex-1 md:pl-14'>
                  {subtextBottom && (
                    <div
                      className={twMerge(
                        'text-center opacity-80 md:text-left',
                        getTextColor()
                      )}
                      style={{ fontSize: '18px' }}
                    >
                      {subtextBottom}
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        )}

        {products && products.length > 0 && (
          <div className='flex w-full justify-center pb-20'>
            <div className='w-full max-w-[1200px] px-4 sm:px-0'>
              <Splide
                options={{
                  type: products.length > 2 ? 'loop' : 'slide',
                  perPage: 2,
                  gap: '2em',
                  pagination: products.length > 2,
                  arrows: products.length > 1,
                  drag: 'free',
                  padding: '20%',
                  breakpoints: {
                    1024: {
                      perPage: 1,
                      pagination: products.length > 1,
                      padding: products.length > 2 ? '20%' : '16%',
                    },
                    768: {
                      perPage: 1,
                      pagination: products.length > 1,
                      padding: products.length > 2 ? '10%' : '16%',
                    },
                    640: {
                      perPage: 1,
                      pagination: products.length > 1,
                      padding: products.length > 2 ? '5%' : '2%',
                    },
                  },
                }}
                aria-label='Product Slider'
              >
                {products.map((product) => (
                  <SplideSlide key={product.id}>
                    <div className='mx-auto w-full max-w-[376px] rounded-[20px] border border-gray-200 bg-white p-9 shadow-sm'>
                      <div className='aspect-square w-full overflow-hidden'>
                        {product.thumbnail?.url && (
                          <img
                            src={product.thumbnail.url}
                            alt={product.model || 'Product Image'}
                            className='h-full w-full object-contain'
                          />
                        )}
                      </div>
                      <div className='mt-6 flex w-full flex-col items-stretch justify-between gap-4'>
                        <Heading size={5} className='text-center text-black'>
                          {product.model}
                        </Heading>
                        <div className='flex flex-col items-center justify-center gap-2'>
                          <p className='text-[20px] font-bold text-[#0071E3]'>
                            ${product.price}
                          </p>
                        </div>
                        <div className='flex justify-center'>
                          <WhereToBuyBtn
                            isMobile={isMobile}
                            setMapOpen={setModalMapOpen}
                            title='Where to Buy'
                            color={
                              pageTheme === 'purple' || pageTheme === 'black'
                                ? 'bg-white text-primary hover:bg-white/90'
                                : 'bg-primary text-white hover:bg-primary-dark'
                            }
                          />
                        </div>
                        {product.coverageArea && (
                          <div className='mt-4 text-center text-[18px] text-black'>
                            Coverage Area:{' '}
                            {product.coverageArea.toLocaleString()} sqft.
                          </div>
                        )}
                      </div>
                    </div>
                  </SplideSlide>
                ))}
              </Splide>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export { EnhancedSlider };
